import {isString} from 'lodash';
import {ellipsis} from 'polished';
import React, {CSSProperties, FC, useCallback, useRef} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import {getEmptyImage} from 'react-dnd-html5-backend';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {SwitcherItem, SwitcherItemProps} from './switcherItem';

/*
 * Props.
 */

export interface DraggableSwitcherItemPublicProps extends SwitcherItemProps {
  index: number;
  frameId: string;
  onDragOver: HandlerOf<string>;
  onDragStart: Handler;
  onDragEnd: Handler;
  isOtherDragging?: boolean;
  isInMultiViewButton?: boolean;
  shouldForceRenderPlaceholder?: boolean;
  label?: React.ReactNode;
}

interface DraggableSwitcherItemProps extends DraggableSwitcherItemPublicProps {
  children: RendererOf<boolean>;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  position: relative;
  max-width: 48px;
`;

const StyledPlaceholderDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: 30px;
  height: 30px;

  pointer-events: none;

  border-radius: 15px;
  background-color: ${(p) => p.theme.alphas.gray20};
`;

const StyledLabelWrapperDiv = styled.div`
  font-size: 9px;
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.light};
  text-align: center;
  width: 100%;
  margin-top: 4px;
  ${ellipsis()};
`;

/*
 * Component.
 */

export const SwitcherDragItemType = 'FRAME_SWITCHER_ITEM';

export const DraggableSwitcherItem: FC<DraggableSwitcherItemProps> = (props) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const {
    frameId,
    index,
    onDragOver,
    onDragStart,
    onDragEnd,
    isOtherDragging,
    children,
    shouldForceRenderPlaceholder,
    label,
    isInMultiViewButton,
  } = props;

  const shouldRenderLabel = Boolean(label && !isInMultiViewButton);

  /*
   * Drag & Drop.
   */

  const onHover = useCallback(
    (item: any) => {
      const itemFrameId = item && item.frameId;
      if (!isString(itemFrameId) || frameId === itemFrameId || index === item.lastOverFrameIndex) {
        return;
      }

      /* eslint no-param-reassign: 0 */
      item.lastOverFrameIndex = index;
      onDragOver(itemFrameId);
    },
    [frameId, index, onDragOver],
  );

  const [, drop] = useDrop({
    accept: SwitcherDragItemType,
    hover: onHover,
  });

  const [{isDragging}, drag, preview] = useDrag(
    () => ({
      type: SwitcherDragItemType,
      item: () => {
        onDragStart();
        return {frameId, children};
      },
      collect: (monitor) => ({isDragging: monitor.isDragging()}),
      end: onDragEnd,
    }),
    [frameId, children, SwitcherDragItemType],
  );

  drag(drop(elementRef));

  // Remove the preview image.
  useInitialEffect(() => {
    preview(getEmptyImage());
  });

  /*
   * Render.
   */

  return (
    <StyledWrapperDiv ref={elementRef}>
      <div style={buildStyles(Boolean(isDragging || shouldForceRenderPlaceholder), isOtherDragging)}>
        <SwitcherItem {...props} renderTooltip={props.isOtherDragging ? undefined : props.renderTooltip}>
          {children(false)}
        </SwitcherItem>
      </div>
      {(isDragging || shouldForceRenderPlaceholder) && <StyledPlaceholderDiv />}
      {shouldRenderLabel && <StyledLabelWrapperDiv>{label}</StyledLabelWrapperDiv>}
    </StyledWrapperDiv>
  );
};

function buildStyles(isDragging: boolean, isOtherDragging: boolean | undefined): CSSProperties | undefined {
  if (isDragging) {
    return {
      opacity: 0,
      pointerEvents: 'none',
    };
  }

  if (isOtherDragging) {
    return {
      pointerEvents: 'none',
    };
  }

  return undefined;
}
